<script lang="ts">
  import Search from '$shared/atoms/Search/Search.svelte';
  import SearchItem from './partials/SearchItem.svelte';
  import type SearchProvider from '$provider/models/SearchProvider';
  import { NoHighlight, moveHighlight, scrollRowIntoView } from '$shared/helpers/listNavigation';

  interface Props {
    searchResult?: SearchProvider[];
    searchLoading?: boolean;
    searchPlaceholder?: string | null;
    action?: import('svelte').Snippet;
    onsearch?: (searchKey: string) => void;
    onclicksearchresult?: (result: SearchProvider) => void;
    onclearsearchresult?: () => void;
  }

  let {
    searchResult = [],
    searchLoading = false,
    searchPlaceholder = null,
    action,
    onsearch,
    onclicksearchresult,
    onclearsearchresult
  }: Props = $props();

  let searchInput: Search;
  let searchParent: HTMLDivElement;
  let searchValue = $state('');
  let listContainer: HTMLElement | undefined = $state();
  let highlightedIndex = $state(NoHighlight);

  const listId = $props.id();
  const optionId = (index: number): string => `${listId}-opt-${index}`;

  const resetSearch = () => {
    searchValue = '';
    highlightedIndex = NoHighlight;
    if (searchInput) searchInput.reset();
  };

  const onSearchChange = (searchKey: string) => {
    searchValue = searchKey;
    highlightedIndex = NoHighlight;
    onsearch?.(searchKey);
  };

  const onClickSearchResult = (result: SearchProvider): void => {
    resetSearch();
    onclicksearchresult?.(result);
  };

  const handleMouseClick = (e: any): void => {
    if (searchParent && !searchParent.contains(e.target)) {
      resetSearch();
      onclearsearchresult?.();
    }
  };

  const setHighlight = (index: number): void => {
    if (index === NoHighlight) return;
    highlightedIndex = index;
    scrollRowIntoView(listContainer, index);
  };

  /**
   * Keyboard handling lives on the wrapper so one listener serves the search
   * input nested inside it. Deliberately NOT on <svelte:window>, and
   * deliberately no tabindex on the wrapper — the input already holds focus and
   * keydown bubbles here.
   */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (!isListOpen) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // Otherwise the arrows just move the caret in the search field.
      e.preventDefault();
      setHighlight(
        moveHighlight(highlightedIndex, selectableIndices, e.key === 'ArrowDown' ? 1 : -1)
      );
      return;
    }

    if (e.key === 'Enter') {
      // Bare Enter is a no-op on purpose: this is a picker with no results
      // route, so auto-selecting the first row would navigate a habit-Enter
      // user to a provider they never looked at. Swallow nothing in that case.
      const result = highlightedIndex === NoHighlight ? undefined : searchList[highlightedIndex];
      if (!result) return;
      e.preventDefault();
      onClickSearchResult(result);
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      // Stop app-level Escape listeners (Modal, CredAgent's SidebarContainer)
      // from also closing their container.
      e.stopPropagation();
      resetSearch();
      onclearsearchresult?.();
    }
  };

  let noResult = $derived(searchValue && searchResult.length === 0);
  let searchList = $derived((searchValue && searchResult) || []);
  let isListOpen = $derived(!searchLoading && (Boolean(noResult) || searchList.length > 0));
  let selectableIndices = $derived(searchList.map((_, index) => index));

  // The results array is replaced ~500ms after the debounced search fires, so
  // an index held from before is meaningless — and silently keeping it would
  // point Enter at a different provider. Resetting in onSearchChange alone is
  // not enough because that runs before the request even starts.
  $effect(() => {
    if (searchResult) {
      highlightedIndex = NoHighlight;
    }
  });
</script>

<svelte:window onmouseup={handleMouseClick} />

<div class="comp-main-actions">
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div bind:this={searchParent} class="action search" onkeydown={onKeyDown}>
    <Search
      bind:this={searchInput}
      minLength={3}
      placeholder={searchPlaceholder}
      loading={searchLoading}
      onchange={onSearchChange}
      name="global-search"
      role="combobox"
      ariaExpanded={isListOpen}
      ariaControls={listId}
      ariaActiveDescendant={highlightedIndex === NoHighlight
        ? undefined
        : optionId(highlightedIndex)}
    />
    {#if !searchLoading}
      <div
        bind:this={listContainer}
        class="search-list"
        class:hidden={!isListOpen}
        data-cy="search-list"
        id={listId}
        role="listbox"
        aria-label="Provider search results"
      >
        {#if noResult}
          <div class="no-result">No results found</div>
        {:else}
          {#each searchList as result, index}
            <SearchItem
              {result}
              highlighted={index === highlightedIndex}
              navIndex={index}
              optionId={optionId(index)}
              onclick={() => {
                onClickSearchResult(result);
              }}
            />
          {/each}
        {/if}
      </div>
    {/if}
  </div>
  {#if action}
    <div class="action">
      {@render action?.()}
    </div>
  {/if}
</div>

<style src="./MainActions.css">
</style>
